import React from 'react';
import { Box, Typography, Card, CardContent, useTheme } from '@mui/material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const PerformanceChart = ({ type, data, title, height = 300, dataKeys, legendNames }) => {
  const theme = useTheme();
  
  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <YAxis 
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[3]
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="concluidas" 
                name="Tarefas Concluídas"
                stroke={theme.palette.primary.main} 
                activeDot={{ r: 8 }} 
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="meta" 
                name="Meta"
                stroke={theme.palette.secondary.main} 
                strokeDasharray="5 5" 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <YAxis 
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[3]
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="meta" 
                name={legendNames?.meta || 'Meta'}
                stroke={theme.palette.secondary.main} 
                fill={theme.palette.secondary.light} 
                fillOpacity={0.3}
                strokeDasharray="5 5"
              />
              <Area 
                type="monotone" 
                dataKey="realizado" 
                name={legendNames?.realizado || 'Realizado'}
                stroke={theme.palette.primary.main} 
                fill={theme.palette.primary.light} 
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <YAxis 
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[3]
                }}
              />
              <Legend />
              {Array.isArray(dataKeys) && dataKeys.length > 0 ? (
                dataKeys.map((key, idx) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    name={key === 'vlr_previsto' ? 'Previsto' : key === 'vlr_realizado_total' ? 'Realizado' : key}
                    fill={idx === 0 ? theme.palette.primary.main : theme.palette.secondary.main}
                    stackId="a"
                    radius={[4, 4, 0, 0]}
                  />
                ))
              ) : (
                <>
                  <Bar 
                    dataKey="produtividade" 
                    name="Produtividade"
                    fill={theme.palette.primary.main} 
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar 
                    dataKey="media" 
                    name="Média Geral"
                    fill={theme.palette.secondary.main} 
                    radius={[4, 4, 0, 0]}
                  />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        const COLORS = [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.info.main,
          theme.palette.success.main,
        ];
        
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[3]
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return <Typography>Tipo de gráfico não suportado</Typography>;
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom component="div">
          {title}
        </Typography>
        <Box sx={{ height: height, width: '100%' }}>
          {renderChart()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerformanceChart;
